// Google Ads Conversion Tracking & Event Relay Utility

import { getAttribution } from './attribution';
import { SpamCheck } from './spamGate';

// Google Ads Account ID
const GOOGLE_ADS_ID = 'AW-825163806';

// Lemay dashboard API for event tracking
const LEMAY_EVENT_API = 'https://lemay.cage.is/api/events';

// Conversion Labels - UPDATE THESE with your actual labels from Google Ads
// To get these: Google Ads → Goals → Conversions → Create conversion action → Get tag
export const CONVERSION_LABELS = {
  PHONE_CLICK: 'IacUCMXj9eYbEJ6AvIkD',
  FORM_SUBMISSION_CONTACT: 'FXrhCIW96eYbEJ6AvIkD',
  FORM_SUBMISSION_QUOTE: 'peXbCMLj9eYbEJ6AvIkD',
};

// Extend window type for gtag
declare global {
  interface Window {
    gtag?: (
      command: 'event' | 'config' | 'js',
      action: string,
      params?: Record<string, unknown>
    ) => void;
    dataLayer?: unknown[];
  }
}

/**
 * Post an event to the Lemay dashboard API.
 * Completely fire-and-forget — failures are silently ignored.
 * Uses sendBeacon for reliability (survives page navigation).
 * This NEVER blocks or affects Google Ads tracking or user experience.
 */
const postEvent = (
  eventType: string,
  page?: string,
  metadata?: Record<string, unknown>
): void => {
  try {
    // Attribution rides along on every event so the dashboard can separate
    // paid traffic from organic without us having to ask Google.
    const attribution = getAttribution();
    const enriched = {
      ...(metadata || {}),
      ...(attribution ? { attribution } : {}),
    };

    const payload = JSON.stringify({
      event_type: eventType,
      page: page || (typeof window !== 'undefined' ? window.location.pathname : null),
      metadata: Object.keys(enriched).length > 0 ? enriched : null,
    });

    if (typeof fetch !== 'undefined') {
      fetch(LEMAY_EVENT_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // Silently ignore — tracking must never break the site
  }
};

/**
 * Track a Google Ads conversion event
 */
export const trackConversion = (conversionLabel: string, value?: number): void => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: `${GOOGLE_ADS_ID}/${conversionLabel}`,
      ...(value !== undefined && { value, currency: 'USD' }),
    });

    // Also log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[Tracking] Conversion: ${conversionLabel}`, value ? `Value: $${value}` : '');
    }
  }
};

/**
 * Track phone number click
 */
export const trackPhoneClick = (): void => {
  trackConversion(CONVERSION_LABELS.PHONE_CLICK);
  trackEvent('contact', {
    event_category: 'Phone',
    event_label: 'Phone Number Click',
    method: 'phone',
  });
  postEvent('phone_click');
};

/**
 * Track contact form submission
 */
export const trackContactFormSubmission = (service?: string, spam?: SpamCheck): void => {
  const suspicious = spam ? spam.verdict !== 'clean' : false;

  // A reported conversion is not just a number in a report, it is training data
  // for Smart Bidding. Reporting a bot teaches the algorithm to buy more traffic
  // that looks like the bot, so suspicious submissions must never fire one.
  if (!suspicious) {
    trackConversion(CONVERSION_LABELS.FORM_SUBMISSION_CONTACT);
    trackEvent('generate_lead', {
      event_category: 'Contact',
      event_label: 'Contact Form Submission',
      service_type: service || 'Not specified',
    });
  }

  // The event still goes to our own dashboard either way. That is the record
  // that lets us see what was caught and re-tune the gate.
  postEvent('form_submit_contact', undefined, {
    service: service || 'Not specified',
    spam_verdict: spam?.verdict || 'clean',
    spam_reasons: spam?.reasons || [],
    conversion_reported: !suspicious,
  });
};

/**
 * Track quote request form submission
 */
export const trackQuoteFormSubmission = (service?: string, spam?: SpamCheck): void => {
  const suspicious = spam ? spam.verdict !== 'clean' : false;

  if (!suspicious) {
    trackConversion(CONVERSION_LABELS.FORM_SUBMISSION_QUOTE);
    trackEvent('generate_lead', {
      event_category: 'Quote',
      event_label: 'Quote Request Submission',
      service_type: service || 'Not specified',
    });
  }

  postEvent('form_submit_quote', undefined, {
    service: service || 'Not specified',
    spam_verdict: spam?.verdict || 'clean',
    spam_reasons: spam?.reasons || [],
    conversion_reported: !suspicious,
  });
};

/**
 * Record a submission the gate refused outright.
 *
 * Nothing reaches Google Forms and no conversion fires, but the attempt is
 * still logged so the block is visible in the dashboard instead of silent.
 */
export const trackBlockedSubmission = (form: 'contact' | 'quote', spam: SpamCheck): void => {
  postEvent(`form_blocked_${form}`, undefined, {
    spam_verdict: spam.verdict,
    spam_reasons: spam.reasons,
    conversion_reported: false,
  });
};

/**
 * Track a CTA button click (navigate to quote/contact page)
 */
export const trackCtaClick = (destination: string, label?: string): void => {
  trackEvent('cta_click', {
    event_category: 'CTA',
    event_label: label || destination,
    destination,
  });
  postEvent('cta_click', undefined, { destination, label: label || destination });
};

/**
 * Track email link click
 */
export const trackEmailClick = (): void => {
  trackEvent('contact', {
    event_category: 'Email',
    event_label: 'Email Link Click',
    method: 'email',
  });
  postEvent('email_click');
};

/**
 * Track a custom event (for Google Analytics)
 */
export const trackEvent = (
  eventName: string,
  params?: Record<string, unknown>
): void => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params);
  }
};
